import React, { forwardRef, ReactNode } from 'react';
import {
    OutlinedInput,
    OutlinedInputProps,
    outlinedInputClasses,
    inputLabelClasses,
    InputLabel,
    FormControl,
    AutocompleteRenderInputParams,
} from '@mui/material';

import { Typography } from '../Typography';


export type SearchProps = Pick<
OutlinedInputProps,
| 'value'
| 'onChange'
| 'onFocus'
| 'onBlur'
| 'onKeyUp'
| 'onKeyDown'
| 'placeholder'
| 'sx'
| 'fullWidth'
| 'inputProps'
| 'label'
> & {
    startIcon?: ReactNode;
    isGray: boolean;
    onClear?: () => void;
    isFocused: boolean;
    onClick: () => void;
    size?: 'large' | 'medium';
    type?: 'number';
    error?: boolean;
};


const LABEL_INDENTS = {
    notFocused: {
        large: -4,
        medium: -8,
    },
    focused: {
        large: 8,
        medium: 4,
    },
};


export const Search2 = forwardRef<HTMLInputElement, SearchProps & Pick<AutocompleteRenderInputParams, 'InputLabelProps'>>(
    ({
        sx = [],
        isFocused,
        isGray,
        onClear,
        InputLabelProps,
        label,
        startIcon,
        size,
        type,
        error,
        ...props
    }, ref) => (
        <FormControl
            fullWidth
            variant='standard'
            sx={{
                overflow: 'hidden',
                '&:hover': {
                    [`& .${inputLabelClasses.root}`]: {
                        borderRadius: 3,
                        bgColor: 'transparent',
                    },
                    [`& .${outlinedInputClasses.root}`]: {
                        bgcolor: isGray
                            ? 'grayscale.shade20'
                            : 'grayscale.shade0',
                    },
                },
            }}
        >
            {label && (
                <InputLabel
                    {...InputLabelProps}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 1,
                        left: 16,
                        pl: 0,
                        top: isFocused || props.value
                            ? LABEL_INDENTS.focused[size]
                            : LABEL_INDENTS.notFocused[size],
                    }}
                >
                    <Typography
                        {...error ? {
                            color: 'infoCustom.red60',
                        } : {
                            color: isFocused ? 'general.secondary50' : 'grayscale.shade85',
                        }}
                        variant='regularBody2'
                    >
                        {label}
                    </Typography>
                </InputLabel>
            )}
            <OutlinedInput
                {...props}
                ref={ref}
                startAdornment={startIcon}
                sx={[
                    {
                        typography: 'regularBody2',
                        borderRadius: 3,
                        bgcolor: isGray ? 'grayscale.shade10' : 'grayscale.shade0',
                        [`& .${outlinedInputClasses.notchedOutline}`]: {
                            border: 1,
                            borderColor: 'transparent',
                        },
                        [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                            borderColor: isGray
                                ? 'transparent'
                                : 'grayscale.shade40',
                        },
                        '&:hover': {
                            bgcolor: isGray
                                ? 'grayscale.shade20'
                                : 'grayscale.shade0',
                        },
                        [`&.${outlinedInputClasses.focused}`]: {
                            bgcolor: isGray
                                ? 'grayscale.shade10'
                                : 'grayscale.shade0',
                        },
                        [`&.${outlinedInputClasses.focused} .${outlinedInputClasses.notchedOutline}`]:
                        {
                            borderColor: 'grayscale.shade60',
                            borderWidth: 1,
                        },
                        [`&.${outlinedInputClasses.root}`]: {
                            paddingLeft: 4,
                        },
                    },
                    ...Array.isArray(sx) ? sx : [sx],
                ]}
            />
        </FormControl>
    ),
);
